import { useState, useEffect } from 'react';
import { getAllFeedback } from '../../services/feedbackService';

const TYPE_CONFIG = {
  bug: {
    icon: 'bug_report',
    label: 'Bug',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
  },
  forslag: {
    icon: 'lightbulb',
    label: 'Forslag',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  sporsmal: {
    icon: 'help',
    label: 'Sporsmal',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
  },
};

const STATUS_CONFIG = {
  submitted: { label: 'Sendt', bg: 'bg-green-100', text: 'text-green-700' },
  pending: { label: 'Ventar', bg: 'bg-yellow-100', text: 'text-yellow-700' },
};

function formatDate(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('nb-NO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function FeedbackList({ onBack }) {
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchFeedback() {
      try {
        setIsLoading(true);
        setError(null);
        const items = await getAllFeedback();
        setFeedbackItems(items);
      } catch (err) {
        console.error('Failed to fetch feedback:', err);
        setError('Kunne ikkje hente tilbakemeldingar');
      } finally {
        setIsLoading(false);
      }
    }
    fetchFeedback();
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="sticky top-0 bg-white px-6 pt-6 pb-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Tilbake"
          >
            <span className="material-symbols-outlined text-gray-500">arrow_back</span>
          </button>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Tilbakemeldingar</h2>
            <p className="text-sm text-gray-500">
              {!isLoading && !error && `${feedbackItems.length} innsendte`}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="overflow-y-auto flex-1 max-h-[60vh]">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-8 h-8 border-3 border-feedback border-t-transparent rounded-full animate-spin mb-3" />
            <p className="text-sm text-gray-500">Hentar tilbakemeldingar...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <span className="material-symbols-outlined text-5xl text-red-200 mb-3">error</span>
            <p className="text-sm text-red-600 font-medium">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 text-sm text-feedback hover:underline"
            >
              Prøv igjen
            </button>
          </div>
        ) : feedbackItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <span className="material-symbols-outlined text-5xl text-gray-200 mb-3">inbox</span>
            <p className="text-sm text-gray-500 font-medium">Ingen tilbakemeldingar enno</p>
            <p className="text-xs text-gray-400 mt-1">Tilbakemeldingar du sender vil dukke opp her</p>
          </div>
        ) : (
          feedbackItems.map((item) => {
            const typeConfig = TYPE_CONFIG[item.type] || TYPE_CONFIG.bug;
            const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;

            return (
              <div
                key={item.id}
                className="px-5 py-4 border-b border-gray-50 last:border-b-0 hover:bg-gray-50/50 transition-colors"
              >
                <div className="flex items-start gap-3">
                  {/* Type icon */}
                  <div className={`shrink-0 flex items-center justify-center size-10 rounded-full ${typeConfig.iconBg}`}>
                    <span className={`material-symbols-outlined text-[20px] ${typeConfig.iconColor}`}>
                      {typeConfig.icon}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">
                        {item.title}
                      </h3>
                      <span className={`shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full ${statusConfig.bg} ${statusConfig.text}`}>
                        {statusConfig.label}
                      </span>
                    </div>

                    {item.description && (
                      <p className="text-xs text-gray-500 line-clamp-2 mb-1">
                        {item.description}
                      </p>
                    )}

                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-[11px] text-gray-400">
                        {formatDate(item.createdAt)}
                      </span>
                      {item.githubIssueUrl && (
                        <a
                          href={item.githubIssueUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] text-feedback hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="material-symbols-outlined text-xs">open_in_new</span>
                          GitHub #{item.githubIssueNumber}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
